import React, { useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';
import useDeliveryDetailService from '../../../app/services/hooks/useDeliveryDetailService';
import useDeliveryService from '../../../app/services/hooks/useDeliveryService';
import useBatchService from '../../../app/services/hooks/useBatchService';
import useSalesOrderDetailService from '../../../app/services/hooks/useSalesOrderDetailService';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { DeliveryDetailDataForm, defaultDeliveryDetailDataForm } from '../../../app/models/DeliveryDetail';
import { Batch } from '../../../app/models/Batch';
import { SalesOrderDetail } from '../../../app/models/SalesOrderDetail';
import { ServiceResponse } from '../../../app/services/shared/interfaces';

import DeliveryDetailForm from './forms/DeliveryDetailForm';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import LazyLoading from '../../../components/LazyLoading';

interface Props {
    deliveryId: number;
    detailId: number;
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const DeliveryDetailEdit = ({ deliveryId, detailId, onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    
    const { editDeliveryDetail, updateDeliveryDetail, fetchingEditDeliveryDetail, fetchingUpdateDeliveryDetail } = 
        useDeliveryDetailService();
    const { showDelivery, fetchingShowDelivery } = useDeliveryService();
    const { getBatches, fetchingGetBatches } = useBatchService();
    const { getSalesOrderDetails, fetchingGetSalesOrderDetails } = useSalesOrderDetailService();

    // Estados
    const [detail, setDetail] = useState<DeliveryDetailDataForm>(defaultDeliveryDetailDataForm);
    const [errorFields, setErrorFields] = useState<any>();
    const [batches, setBatches] = useState<Batch[]>([]);
    const [salesOrderDetails, setSalesOrderDetails] = useState<SalesOrderDetail[]>([]);
    const [salesOrderId, setSalesOrderId] = useState<number | null>(null);

    useEffect(() => {
        loadDelivery();
        edit();
    }, [detailId]);

    useEffect(() => {
        if (salesOrderId) {
            loadSalesOrderDetails();
        }
    }, [salesOrderId]);

    useEffect(() => {
        if (detail.sales_order_detail_id && salesOrderDetails.length > 0) {
            loadBatches();
        }
    }, [detail.sales_order_detail_id, salesOrderDetails]);

    const loadDelivery = () => {
        showDelivery(deliveryId, {
            onSuccess: (response: ServiceResponse) => {
                const delivery = response.data.delivery;
                if (delivery.sales_order_id) {
                    setSalesOrderId(delivery.sales_order_id);
                }
            },
            onError: (error) => {
                console.error('Error al cargar entrega:', error);
            }
        });
    };

    const loadSalesOrderDetails = () => {
        if (!salesOrderId) return;
        
        getSalesOrderDetails(
            {
                page: 1,
                per_page: 1000,
                sort: 'position',
                order: 'asc',
                sales_order_id: salesOrderId
            },
            {
                onSuccess: (response: ServiceResponse) => {
                    setSalesOrderDetails(response.data?.sales_order_details || []);
                },
                onError: (error) => {
                    console.error('Error al cargar posiciones del pedido:', error);
                }
            }
        );
    };

    const loadBatches = () => {
        if (!detail.sales_order_detail_id) {
            setBatches([]);
            return;
        }

        const selectedDetail = salesOrderDetails.find(d => d.id === detail.sales_order_detail_id);
        if (!selectedDetail || !selectedDetail.product_id) {
            setBatches([]);
            return;
        }

        getBatches(
            {
                page: 1,
                per_page: 1000,
                sort: 'id',
                order: 'desc',
                is_active: true,
                product_id: selectedDetail.product_id || null
            },
            {
                onSuccess: (response: ServiceResponse) => {
                    setBatches(response.data?.batches || []);
                },
                onError: (error) => {
                    console.error('Error al cargar lotes:', error);
                    toast.error('Error al cargar los lotes disponibles');
                }
            }
        );
    };

    const edit = () => {
        editDeliveryDetail(deliveryId, detailId, {
            onSuccess: (response: ServiceResponse) => {
                const fetchedDetail = response.data.delivery_detail;
                setDetail({
                    id: fetchedDetail.id,
                    delivery_id: deliveryId,
                    sales_order_detail_id: fetchedDetail.sales_order_detail_id,
                    batch_id: fetchedDetail.batch_id,
                    quantity: fetchedDetail.quantity,
                    unit_id: fetchedDetail.unit_id,
                    position: fetchedDetail.position,
                    observations: fetchedDetail.observations,
                    price: fetchedDetail.price,
                    price_currency: fetchedDetail.price_currency,
                    quantity_unit: fetchedDetail.quantity_unit,
                    price_unit: fetchedDetail.price_unit
                });
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (onError) onError();
            }
        });
    };

    const update = () => {
        // Validar que se haya seleccionado una posición del pedido
        if (!detail.sales_order_detail_id) {
            setErrorFields({ sales_order_detail_id: ['Debe seleccionar una posición del pedido'] });
            return;
        }

        // Validar que se haya seleccionado un lote
        if (!detail.batch_id) {
            setErrorFields({ batch_id: ['Debe seleccionar un lote'] });
            return;
        }

        // Validar cantidad básica
        if (!detail.quantity || Number(detail.quantity) <= 0) {
            setErrorFields({ quantity: ['La cantidad debe ser mayor a 0'] });
            return;
        }

        const selectedBatch = batches.find(b => b.id === detail.batch_id);
        const selectedOrderDetail = salesOrderDetails.find(d => d.id === detail.sales_order_detail_id);

        // Validar que la unidad de la posición del pedido coincida con la unidad del lote
        if (selectedBatch && selectedOrderDetail) {
            const orderUnit = (selectedOrderDetail.quantity_unit || '').toString().trim().toLowerCase();
            const batchUnit = (selectedBatch.unit || '').toString().trim().toLowerCase();
            if (orderUnit && batchUnit && orderUnit !== batchUnit) {
                useReactConfirmAlert().errorAlert({
                    title: 'Unidades distintas',
                    message: `La unidad de la posición del pedido (${selectedOrderDetail.quantity_unit || 'N/A'}) no coincide con la unidad del lote (${selectedBatch.unit || 'N/A'}). Debe seleccionar un lote con la misma unidad que la posición del pedido.`
                });
                return;
            }
        }

        // Validación: la cantidad no puede exceder la cantidad del lote
        if (selectedBatch) {
            const batchQuantity = Number(selectedBatch.quantity) || 0;
            const requestedQuantity = Number(detail.quantity) || 0;
            if (requestedQuantity > batchQuantity) {
                setErrorFields({ 
                    quantity: [`La cantidad no puede exceder la cantidad del lote (${batchQuantity} ${selectedBatch.unit || ''})`] 
                });
                return;
            }
        }

        if (showLoading) showLoading('loading', 'Actualizando detalle...');

        updateDeliveryDetail(deliveryId, detailId, detail, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                toast.success(response.message, { autoClose: 2500 });
                setErrorFields(undefined);
                edit();
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    if (fetchingShowDelivery || fetchingGetSalesOrderDetails || fetchingGetBatches || fetchingEditDeliveryDetail) {
        return <LazyLoading height={300} />;
    }

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <DeliveryDetailForm
                        detail={detail}
                        setDetail={setDetail}
                        batches={batches}
                        salesOrderDetails={salesOrderDetails}
                        errorFields={errorFields}
                        is_edit={true}
                    />
                </div>
            </div>
            
            <hr />
            
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm 
                        callbackCancel={cancel} 
                        locked={fetchingUpdateDeliveryDetail} 
                    />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm 
                        callbackSave={update} 
                        locked={fetchingUpdateDeliveryDetail} 
                    />
                </div>
            </div>
        </>
    );
};

export default DeliveryDetailEdit;
